import { Component, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AgeAmountLimitDto } from '../../../../core/data/new-dtos/insurance-type.dtos';

/**
 * Edits the product's maximum sum assured by age band and currency
 * (e.g. 57-99 years: DOP 2,750,000 / USD 50,000).
 */
@Component({
  selector: 'app-age-amount-limit-selector',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective],
  templateUrl: './age-amount-limit-selector.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgeAmountLimitSelectorComponent),
      multi: true,
    },
    provideNgxMask(),
  ],
})
export class AgeAmountLimitSelectorComponent implements ControlValueAccessor {
  readonly currencies = ['DOP', 'USD'];
  readonly form: FormGroup;
  disabled = false;

  private isWriteValue = false;
  private onChange: (value: AgeAmountLimitDto[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly fb: FormBuilder) {
    this.form = this.fb.group({ limits: this.fb.array([]) });
    this.form.valueChanges.subscribe(() => {
      if (this.isWriteValue) return;
      this.onChange(this.toDtos());
      this.onTouched();
    });
  }

  get limits(): FormArray {
    return this.form.get('limits') as FormArray;
  }

  add(): void {
    this.limits.push(this.createGroup());
  }

  remove(index: number): void {
    this.limits.removeAt(index);
  }

  writeValue(value: AgeAmountLimitDto[] | null): void {
    this.isWriteValue = true;
    this.limits.clear();
    (value ?? []).forEach((l) => this.limits.push(this.createGroup(l)));
    this.isWriteValue = false;
  }

  registerOnChange(fn: (value: AgeAmountLimitDto[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) this.form.disable({ emitEvent: false });
    else this.form.enable({ emitEvent: false });
  }

  private createGroup(l?: AgeAmountLimitDto): FormGroup {
    return this.fb.group({
      ageFrom: [l?.ageFrom ?? null, Validators.required],
      ageTo: [l?.ageTo ?? null, Validators.required],
      currency: [l?.currency ?? 'DOP', Validators.required],
      maxAmount: [l?.maxAmount ?? null, Validators.required],
    });
  }

  private toDtos(): AgeAmountLimitDto[] {
    return this.limits.getRawValue().map((r: any) => ({
      ageFrom: Number(r.ageFrom ?? 0),
      ageTo: Number(r.ageTo ?? 0),
      currency: r.currency,
      maxAmount: Number(r.maxAmount ?? 0),
    }));
  }
}
